'use client'

import { forwardRef, useId, useState } from 'react'

import { LuEye, LuEyeOff } from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import type { InputHTMLAttributes } from 'react'

type InputVariant = 'outline' | 'underline'

type PasswordInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type'
> & {
  label?: string
  error?: string
  helperText?: string
  variant?: InputVariant
  classNames?: {
    container?: string
    input?: string
    label?: string
    error?: string
  }
}

const variantStyles: Record<InputVariant, string> = {
  outline:
    'h-11 rounded-field border border-transparent bg-surface-raised px-4 focus-within:border-point',
  underline:
    'h-12 border-b border-border bg-transparent px-1 focus-within:border-point',
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    { label, error, helperText, variant = 'outline', id, classNames, ...props },
    ref
  ) => {
    const [isVisible, setIsVisible] = useState(false)
    const generatedId = useId()
    const inputId = id || props.name || generatedId
    const helperTextId = helperText ? `${inputId}-helper` : undefined
    const errorId = error ? `${inputId}-error` : undefined

    return (
      <div className={cn(classNames?.container)}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'mb-2 block text-c14 font-medium text-foreground',
              classNames?.label
            )}
          >
            {label}
          </label>
        )}
        <div
          className={cn(
            'flex w-full items-center gap-2 transition-colors duration-200',
            variantStyles[variant],
            error && 'border-error focus-within:border-error'
          )}
        >
          <input
            ref={ref}
            id={inputId}
            type={isVisible ? 'text' : 'password'}
            aria-invalid={Boolean(error)}
            aria-describedby={errorId ?? helperTextId}
            className={cn(
              'min-w-0 flex-1 bg-transparent text-c15 text-foreground outline-none',
              'placeholder:text-description/70',
              classNames?.input
            )}
            {...props}
          />
          <button
            type="button"
            onClick={() => setIsVisible((previousVisible) => !previousVisible)}
            data-focus-appearance="fill"
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-control text-description',
              'transition-colors duration-200 hover:bg-surface-strong hover:text-foreground',
              'focus:outline-none focus-visible:bg-surface-strong'
            )}
            aria-label={isVisible ? '비밀번호 숨기기' : '비밀번호 보기'}
            aria-pressed={isVisible}
          >
            {isVisible ? (
              <LuEyeOff aria-hidden="true" className="size-5" />
            ) : (
              <LuEye aria-hidden="true" className="size-5" />
            )}
          </button>
        </div>
        {helperText && !error && (
          <p
            id={helperTextId}
            className="mt-2 text-c12 leading-5 text-description"
          >
            {helperText}
          </p>
        )}
        {error && (
          <p
            id={errorId}
            role="alert"
            className={cn(
              'mt-2 text-c12 leading-5 text-error',
              classNames?.error
            )}
          >
            {error}
          </p>
        )}
      </div>
    )
  }
)

PasswordInput.displayName = 'PasswordInput'
